import { HoverCard } from '@mantine/core'
import { Step } from 'types/graphql'

import { useQuery } from '@redwoodjs/web'

import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import { GET_APPLICATION_BY_APPLICANT_ID } from 'src/graphql/application.graphql'
import { GET_MVR_FINDINGS } from 'src/graphql/documents.graphql'
import { DocumentStatus } from 'src/graphql/types/documents'
import { StepType } from 'src/graphql/types/steps'
import IconFlag from 'src/icons/IconFlag'
import IconSparkle from 'src/icons/IconSparkle'
import {
  MVR_COUNTED_FINDING_TYPES,
  mvrFindingsFromExtractedRows,
  MvrExtractedFinding,
} from 'src/lib/documentIntelligence'

import {
  bestPredictionsByField,
  cdlHasValue,
  ComparisonFlag,
  evaluateRules,
  Flag,
  FLAG_RULES,
  LicenseValues,
  medicalHasValue,
  MvrScalarRow,
} from '../../../tabs/ApplicationTab/components/AiFlagsPill/aiFlagsRules'

// Checklist items that surface AI document findings:
//   • CDL / DOT Medical — scalar predictions (category, expiration) the
//     recruiter hasn't reviewed yet.
//   • MVR — AI-extracted accidents / violations / suspensions off the report.
//   • PSP — AI-extracted crashes / violations off the FMCSA report. Admin-only
//     during the rollout; non-admin users skip the PSP path entirely.
const FINDINGS_STEP_TYPES = new Set<string>([
  StepType.CDL,
  StepType.DOT_MEDICAL,
  StepType.MVR,
  StepType.PSP,
])

// "Pending review" palette (doubleNickel-warning token) shared with AiFlagsPill
// and the License card's AiMismatchChip so every AI-finding surface reads as the
// same severity as a pending-review status.
const CHIP_BG = '#FFFAEB' // warning-50
const CHIP_BORDER = '#FEDF89' // warning-200
const CHIP_TEXT = '#B54708' // warning-700

type StepDocument = Step['documents'][number]
type AiExtractedRow = NonNullable<
  NonNullable<StepDocument['documentIntelligence']>['aiExtractedRows']
>[number]

// Rows the badge leaves out of its incident count, even though the backend
// read-model includes them:
//   • Med cert category / expiration — credential details, not incidents.
// ENG-4346: License actions (LICENSE_ACTIONS) used to live in this exclusion
// set on the rationale that cancellations/reinstatements are "administrative,
// not incidents." Product decision reversed: cancellations and revocations
// carry the same recruiter-review weight as suspensions/disqualifications,
// and the AiFlagsPill on the Application tab already surfaces them. Keeping
// them out of the checklist badge produced a "2 findings" flag next to a
// popover that included a third license-action finding, which recruiters
// found misleading. License actions are now counted.
// Scalar rows like Category/Expiration are excluded by the records check
// below rather than listed here, since there are many of them.
const BADGE_EXCLUDED_FIELDS = new Set([
  'MVR_MEDICAL_CERT_CATEGORY',
  'MVR_MEDICAL_CERT_EXPIRATION',
  // ENG-4335: exclude PSP inspection count rows too. Inspections aren't
  // self-disclosable events on the driver's application — a clean roadside
  // inspection isn't "a finding to review" the way a crash or violation is.
  // Mirrors the AiFlagsPill / PspFindingsPill scope where INSPECTION is
  // omitted from the incident-count rollup.
  'PSP_INSPECTIONS',
])

// A finding-count row (Accidents, Moving violations, Suspensions…) carries one
// `record` per individual finding; scalar rows (Category, Expiration, med cert…)
// carry none. So this selects the incident counts the badge sums: a row with
// records that isn't one of the explicitly-excluded non-incident fields. Any new
// finding family the backend registry adds flows through with no frontend change.
const isFindingCountRow = (row: AiExtractedRow): boolean =>
  (row.records?.length ?? 0) > 0 && !BADGE_EXCLUDED_FIELDS.has(row.field)

interface BadgeRow {
  key: string
  label: string
  value: string
}

interface BadgeGroup {
  key: string
  // File name, shown only when findings span multiple source documents.
  title?: string
  rows: BadgeRow[]
}

interface BadgeData {
  count: number
  heading: string
  groups: BadgeGroup[]
}

// CDL / DOT Medical: run the shared FLAG_RULES engine against the application's
// saved license values + this step's documents. Counts the number of
// per-field mismatches in the group ('cdl' or 'medical') — matching what
// AiFlagsPill and the License card chips would surface for the same data.
//
// Pre-engine, this used getPendingAiPredictions which (a) only counted DATE +
// CATEGORY predictions, missing license-number/state/class/endorsements
// mismatches, and (b) compared against the document's own scalars instead of
// the application's License card values. Both gaps are now closed.
const getPredictionBadge = (
  docs: StepDocument[],
  license: LicenseValues | null | undefined,
  groupKey: 'cdl' | 'medical',
  // MVR scalars + their document id — fed in by the parent component when
  // available. The medical-expiration rule's mvr-scalar source uses these so
  // the DOT_MEDICAL badge surfaces an MVR_MEDICAL_CERT_EXPIRATION disagreement
  // with the application, matching AiFlagsPill and the License card chip.
  // Empty by default (no MVR query was issued, or no scalar row present).
  mvrScalarRows: MvrScalarRow[] = [],
  mvrScalarDocumentId: string | null = null,
  // MVR existence findings + their document id — fed in for the CDL badge so
  // the license-ever-suspended ContradictedAbsenceRule fires when the driver
  // answered No but the MVR has a SUSPENSION / LICENSE_ACTION. Empty by
  // default; the engine silently no-ops for rules whose group doesn't match.
  mvrFindings: MvrExtractedFinding[] = [],
  mvrFindingsDocumentId: string | null = null
): BadgeData | null => {
  // Only completed-extraction docs are reviewable. AiFlagsPill and the License
  // card both filter on documentIntelligence.status === 'COMPLETED' before
  // feeding predictions into the engine; the badge has to match or surfaces
  // disagree during the upload's PROCESSING window.
  const completedDocs = docs.filter(
    (doc) => doc.documentIntelligence?.status === 'COMPLETED'
  )
  // The step's type already determines the document category — every doc on a
  // CDL step is a CDL doc, every doc on a DOT_MEDICAL step is a DOT Med doc.
  // So we don't filter on doc.category here; we feed predictions through the
  // category lane that matches the step. This keeps the function working with
  // step.documents fixtures that don't bother to set doc.category.
  const cdlPredictions =
    groupKey === 'cdl'
      ? bestPredictionsByField(completedDocs, cdlHasValue)
      : {}
  const medicalPredictions =
    groupKey === 'medical'
      ? bestPredictionsByField(completedDocs, medicalHasValue)
      : {}
  const flags = evaluateRules(FLAG_RULES, {
    license: license ?? undefined,
    cdlPredictions,
    medicalPredictions,
    mvrScalarRows,
    mvrScalarDocumentId,
    mvrFindings,
    mvrFindingsDocumentId,
  }).filter(
    (f): f is ComparisonFlag =>
      f.kind === 'comparison' && f.group === groupKey
  )

  if (flags.length === 0) return null

  return {
    count: flags.length,
    heading:
      flags.length === 1
        ? '1 finding to review'
        : `${flags.length} findings to review`,
    groups: [
      {
        key: groupKey,
        rows: flags.map((f) => ({
          key: f.key,
          label: f.label,
          value: f.mismatches[0]?.aiValue ?? '',
        })),
      },
    ],
  }
}

// Finding-count badge for MVR / PSP steps: count the AI-read accidents /
// violations / suspensions (MVR) or crashes / violations (PSP) off the step's
// documents, with a per-category breakdown in the popover.
//
// Scoped to a SINGLE document — the newest MVR/PSP with completed AI analysis
// and at least one finding (docs arrive createdAt desc from the Step.documents
// resolver). A driver may upload multiple reports (a re-pull, or front/back),
// and each carries its own full set of findings; flat-mapping across all of
// them double-counts incidents. This mirrors MvrFindingsPill / PspFindingsPill
// and the DocumentEdit "AI Extracted" popover, both of which read off one
// document, so the surfaces always agree. INSPECTION (PSP) is excluded via
// BADGE_EXCLUDED_FIELDS — not a self-disclosable event.
const getMvrBadge = (docs: StepDocument[]): BadgeData | null => {
  const countRowsFor = (doc: StepDocument): AiExtractedRow[] =>
    (doc.documentIntelligence?.aiExtractedRows ?? []).filter(isFindingCountRow)

  const latestWithFindings = docs.find(
    (doc) =>
      doc.documentIntelligence?.status === 'COMPLETED' &&
      countRowsFor(doc).length > 0
  )
  if (!latestWithFindings) return null

  const countRows = countRowsFor(latestWithFindings)
  // The badge count is the total number of incidents, summed across the
  // per-category count rows (each row's value is its count).
  const count = countRows.reduce((n, row) => n + (Number(row.value) || 0), 0)
  if (count === 0) return null

  return {
    count,
    heading: count === 1 ? '1 finding' : `${count} findings`,
    groups: [
      {
        key: 'mvr',
        rows: countRows.map((row) => ({
          key: row.field,
          label: row.label,
          value: row.value,
        })),
      },
    ],
  }
}

// "Count badge" (design D): a flag + count on the step label when the AI has
// pulled values off the step's documents that the recruiter hasn't reviewed
// yet. The status pill is left untouched. Hovering reveals the findings.
//
// ENG-4328: the component owns BOTH sides of every comparison rule internally
// — the application license is fetched here alongside the MVR data, rather
// than being passed as a prop. Requiring only `applicantId` from callers
// eliminates the class of bug where a caller (e.g. DQFContent pre-fix) forgot
// to pass the license and every rule fell through to a "Not set" false
// positive. All callers pass the same applicantId, Apollo dedupes across
// instances, and the badge is impossible to invoke with lopsided data.
const StepFindingsBadge = ({
  step,
  applicantId,
}: {
  step: Step
  // The applicant this step belongs to. Required because the badge fetches
  // the application license AND MVR data internally, both keyed on this id.
  // If you don't have an applicantId, don't render the badge.
  applicantId: string
}) => {
  const currentCompany = useCurrentCompanyContext()
  const showAi = currentCompany?.documentIntelligenceEnabled ?? false

  // Application license and MVR data are only needed for the rule-engine
  // paths (CDL and DOT_MEDICAL). The MVR step badge reads its own document's
  // aiExtractedRows and doesn't consult either query.
  const needsRuleEngine =
    step.type === StepType.CDL || step.type === StepType.DOT_MEDICAL

  // Application license — the "application side" of every CDL/DOT_MEDICAL
  // comparison rule. Same query AiFlagsPill's parent (ApplicationTab) loads,
  // so Apollo dedupes when both are mounted. UPDATE_APPLICATION already lists
  // this query in refetchQueries, so the badge refreshes automatically when
  // the recruiter saves the License card.
  const { data: applicationData } = useQuery(GET_APPLICATION_BY_APPLICANT_ID, {
    variables: { applicantId },
    skip: !showAi || !needsRuleEngine,
    fetchPolicy: 'cache-and-network',
  })
  const license = (applicationData?.applicationByApplicantId?.license ??
    null) as LicenseValues | null

  // MVR data feeds two rule-engine paths — DOT_MEDICAL via mvrScalarRows
  // (medical-cert expiration), CDL via mvrFindings (the ENG-4273
  // license-ever-suspended ContradictedAbsenceRule). Apollo dedupes against
  // AiFlagsPill's identical query on the same applicantId. Hook order: keep
  // the useQuery before any early returns.
  const { data: mvrData } = useQuery(GET_MVR_FINDINGS, {
    variables: { applicantId },
    skip: !showAi || !needsRuleEngine,
    fetchPolicy: 'cache-and-network',
  })

  if (!showAi || !FINDINGS_STEP_TYPES.has(step.type)) return null
  // Hold rendering until the license query has produced data for
  // rule-engine paths. Gating on `!applicationData` (not on `loading`)
  // covers both the loading window AND the error path — a failed
  // GET_APPLICATION_BY_APPLICANT_ID (network / GraphQL error) leaves
  // `applicationData` undefined while `loading` flips to false, and
  // evaluating rules against `license = null` at that point would
  // recreate the ENG-4328 false-positive class this gate is meant to
  // prevent. MVR steps skip the gate — they don't consult the license.
  if (needsRuleEngine && !applicationData) return null

  const activeDocs = step.documents.filter(
    (doc) => doc.status !== DocumentStatus.ARCHIVED
  )

  // Newest completed MVR's scalar rows (latest report is the source of truth,
  // matching AiFlagsPill / License card). Used by the DOT_MEDICAL badge.
  let mvrScalarRows: MvrScalarRow[] = []
  let mvrScalarDocumentId: string | null = null
  if (step.type === StepType.DOT_MEDICAL) {
    for (const doc of mvrData?.documents?.items ?? []) {
      if (doc.documentIntelligence?.status !== 'COMPLETED') continue
      mvrScalarRows = (doc.documentIntelligence.aiExtractedRows ?? [])
        .filter((row) => !row.records || row.records.length === 0)
        .map((row) => ({ field: row.field, value: row.value ?? null }))
      mvrScalarDocumentId = doc.documentId
      break
    }
  }

  // MVR existence findings for the CDL badge — walks newest-first and picks
  // the first MVR carrying any surfaced-type findings (matches AiFlagsPill /
  // License card selectMvrForExistence). Without this, the license-ever-
  // suspended rule's contradicted-absence flag never fires on the CDL
  // checklist badge even though pill + card surface it.
  let mvrFindings: MvrExtractedFinding[] = []
  let mvrFindingsDocumentId: string | null = null
  if (step.type === StepType.CDL) {
    for (const doc of mvrData?.documents?.items ?? []) {
      if (doc.documentIntelligence?.status !== 'COMPLETED') continue
      const f = mvrFindingsFromExtractedRows(
        doc.documentIntelligence.aiExtractedRows ?? []
      ).filter((finding) =>
        MVR_COUNTED_FINDING_TYPES.includes(finding.findingType)
      )
      if (f.length > 0) {
        mvrFindings = f
        mvrFindingsDocumentId = doc.documentId
        break
      }
    }
  }

  // getMvrBadge is generic over any document type whose aiExtractedRows carry
  // finding-count rows — it sums count-row values on the newest completed
  // doc. Both MVR and PSP steps feed through it. For PSP, BADGE_EXCLUDED_FIELDS
  // filters out PSP_INSPECTIONS so the count reflects crashes + violations
  // only (matching the AiFlagsPill scope).
  const data =
    step.type === StepType.MVR || step.type === StepType.PSP
      ? getMvrBadge(activeDocs)
      : getPredictionBadge(
          activeDocs,
          license,
          step.type === StepType.CDL ? 'cdl' : 'medical',
          mvrScalarRows,
          mvrScalarDocumentId,
          mvrFindings,
          mvrFindingsDocumentId
        )

  if (!data) return null

  return (
    <HoverCard
      width={240}
      shadow="md"
      withArrow
      position="bottom-start"
      openDelay={100}
      classNames={{ dropdown: 'p-0' }}
    >
      <HoverCard.Target>
        <span
          data-testid="step-findings-badge"
          className="ml-1.5 inline-flex h-4 items-center gap-0.5 rounded-full border px-1.5 text-[10px] font-semibold leading-none no-underline"
          style={{
            backgroundColor: CHIP_BG,
            borderColor: CHIP_BORDER,
            color: CHIP_TEXT,
          }}
        >
          <IconFlag className="h-2.5 w-2.5" />
          {data.count}
        </span>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <div className="flex flex-col gap-2 p-2 text-xs">
          <div className="flex items-center gap-1 font-semibold text-doubleNickel-brand-500">
            <IconSparkle size={11} />
            {data.heading}
          </div>
          {data.groups.map((group) => (
            <div key={group.key} className="flex flex-col gap-1">
              {group.title && (
                <div className="truncate font-medium text-doubleNickel-gray-500">
                  {group.title}
                </div>
              )}
              {group.rows.map((row) => (
                <div
                  key={row.key}
                  className="flex items-center justify-between gap-3"
                >
                  <span className="text-doubleNickel-gray-500">
                    {row.label}
                  </span>
                  <span className="font-medium text-doubleNickel-gray-800">
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </HoverCard.Dropdown>
    </HoverCard>
  )
}

export default StepFindingsBadge
